import type { SibValueChangeDto, ValueChangeType } from '../../../api/dto/unionChanges.dto'
import type { QuantityUnit } from './capacity.types'

export const CHANGE_TYPE_LABEL: Record<ValueChangeType, string> = {
  PRICE: 'Preis',
  CAPACITY: 'Kapazität',
}

/** The unit a capacity is counted in, per service code: the same units the dashboard uses. */
export const CAPACITY_UNIT_BY_SERVICE_CODE: Record<string, QuantityUnit> = {
  TOJ: 'TOJ',
  CONSULTING: 'Std.',
  TRAINING: 'Tr.',
  PILOT: 'Std.',
}

const EMPTY_VALUE = '—'

/**
 * The `validFrom` the backend backfill (SIB-939) gives a value that has been in effect since
 * before versioning existed. It means "has always been this", not a real date, so it is never
 * shown as one; the settings pages read it the same way.
 */
export const BASELINE_VALID_FROM = '1970-01-01'
export const BASELINE_VALID_FROM_LABEL = 'von Anfang an'

export function isBaselineDate(isoDate: string | null | undefined): boolean {
  return isoDate === BASELINE_VALID_FROM
}

/** Stable identity of a change across the two version tables. */
export function changeKey(change: Pick<SibValueChangeDto, 'changeType' | 'changeId'>): string {
  return `${change.changeType}-${change.changeId}`
}

/**
 * A price as euros, a capacity with its unit. Zero is a value and renders as one; only null
 * (no previous value) renders as a dash.
 */
export function formatChangeValue(
  value: number | null | undefined,
  changeType: ValueChangeType,
  serviceCode: string
): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE
  if (changeType === 'PRICE') {
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
  }
  const amount = value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
  const unit = CAPACITY_UNIT_BY_SERVICE_CODE[serviceCode]
  return unit ? `${amount} ${unit}` : amount
}

/** `yyyy-MM-dd` to a local Date at midnight. Avoids the UTC shift of `new Date('yyyy-MM-dd')`. */
export function parseIsoDate(isoDate: string | null | undefined): Date | null {
  if (!isoDate) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

/** Local Date to `yyyy-MM-dd`, the form the backend's `LocalDate` parameters expect. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** An effective date for display: the baseline reads "von Anfang an", everything else as a date. */
export function formatChangeDate(isoDate: string | null | undefined): string {
  if (isBaselineDate(isoDate)) return BASELINE_VALID_FROM_LABEL
  const date = parseIsoDate(isoDate)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat('de-DE').format(date)
}

/**
 * `LocalDateTime` from the backend to `dd.MM.yyyy | HH:mm Uhr`, the format the order history
 * uses. The string carries no offset and is parsed as local time, which matches how it was
 * written.
 */
export function formatChangeTimestamp(isoDateTime: string | null | undefined): string {
  if (!isoDateTime) return EMPTY_VALUE
  const date = new Date(isoDateTime)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  const day = new Intl.DateTimeFormat('de-DE').format(date)
  const time = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(
    date
  )
  return `${day} | ${time} Uhr`
}

export function isFutureDate(isoDate: string | null | undefined, today = new Date()): boolean {
  if (isBaselineDate(isoDate)) return false
  const date = parseIsoDate(isoDate)
  if (!date) return false
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return date.getTime() > startOfToday.getTime()
}

/**
 * The dashboard's "current period" is the calendar year: its capacity endpoints take a `year`
 * and default to the current one on the backend. The change list uses the same meaning.
 */
export function currentPeriod(today = new Date()): { from: string; to: string } {
  const year = today.getFullYear()
  return { from: toIsoDate(new Date(year, 0, 1)), to: toIsoDate(new Date(year, 11, 31)) }
}

// ---------------------------------------------------------------------------------------------
// Toolbar: period presets, search, filters and sorting. The list loads every change of the
// period and applies these in the browser, like the SIB and bank tables.
// ---------------------------------------------------------------------------------------------

export type PeriodPreset = 'currentYear' | 'last30Days' | 'last90Days' | 'previousYear' | 'all'

export const PERIOD_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: 'currentYear', label: 'Aktuelles Jahr' },
  { value: 'last30Days', label: 'Letzte 30 Tage' },
  { value: 'last90Days', label: 'Letzte 90 Tage' },
  { value: 'previousYear', label: 'Vorjahr' },
  { value: 'all', label: 'Gesamter Zeitraum' },
]

/** The `from`/`to` bounds of a preset; empty strings mean no bound. */
export function periodRange(
  preset: PeriodPreset,
  today = new Date()
): { from: string; to: string } {
  const year = today.getFullYear()
  const startOfToday = new Date(year, today.getMonth(), today.getDate())
  const daysAgo = (days: number) =>
    new Date(startOfToday.getFullYear(), startOfToday.getMonth(), startOfToday.getDate() - days)
  switch (preset) {
    case 'currentYear':
      return currentPeriod(today)
    case 'last30Days':
      return { from: toIsoDate(daysAgo(29)), to: toIsoDate(startOfToday) }
    case 'last90Days':
      return { from: toIsoDate(daysAgo(89)), to: toIsoDate(startOfToday) }
    case 'previousYear':
      return {
        from: toIsoDate(new Date(year - 1, 0, 1)),
        to: toIsoDate(new Date(year - 1, 11, 31)),
      }
    case 'all':
      return { from: '', to: '' }
  }
}

export type ChangeTypeFilter = ValueChangeType | 'all'
export type SeenFilter = 'unseen' | 'all'

export const CHANGE_TYPE_FILTERS: { value: ChangeTypeFilter; label: string }[] = [
  { value: 'all', label: 'Alle Typen' },
  { value: 'PRICE', label: CHANGE_TYPE_LABEL.PRICE },
  { value: 'CAPACITY', label: CHANGE_TYPE_LABEL.CAPACITY },
]

export const SEEN_FILTERS: { value: SeenFilter; label: string }[] = [
  { value: 'unseen', label: 'Ungesehen' },
  { value: 'all', label: 'Gesehen und ungesehen' },
]

export interface ChangeListFilters {
  search: string
  type: ChangeTypeFilter
  seen: SeenFilter
}

/** Free-text match on the names a coordinator would type: SIB, service, who changed, who saw. */
export function matchesChangeSearch(change: SibValueChangeDto, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    change.sibName,
    change.serviceName,
    change.serviceCode,
    change.changedByName,
    change.seen?.seenByName,
  ].some(value => (value ?? '').toLowerCase().includes(q))
}

export function filterChanges(
  items: SibValueChangeDto[],
  { search, type, seen }: ChangeListFilters
): SibValueChangeDto[] {
  return items.filter(
    change =>
      matchesChangeSearch(change, search) &&
      (type === 'all' || change.changeType === type) &&
      (seen === 'all' || change.seen == null)
  )
}

export type ChangeSortField = 'changedAt' | 'sib' | 'service' | 'type' | 'validFrom' | 'seenAt'
export type ChangeSortDir = 'asc' | 'desc'

export const CHANGE_SORT_OPTIONS: { value: ChangeSortField; label: string }[] = [
  { value: 'changedAt', label: 'Geändert am' },
  { value: 'sib', label: 'Alphabetisch' },
  { value: 'service', label: 'Leistung' },
  { value: 'type', label: 'Typ' },
  { value: 'validFrom', label: 'Gültig ab' },
  { value: 'seenAt', label: 'Gesehen am' },
]

function lastNameOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  return parts[parts.length - 1] ?? ''
}

/** Newest first for `changedAt` desc, the list's default. A stable copy; the input is untouched. */
export function sortChanges(
  items: SibValueChangeDto[],
  field: ChangeSortField,
  dir: ChangeSortDir
): SibValueChangeDto[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    let cmp = 0
    switch (field) {
      case 'changedAt':
        cmp = a.changedAt.localeCompare(b.changedAt)
        break
      case 'sib':
        cmp = lastNameOf(a.sibName).localeCompare(lastNameOf(b.sibName), 'de')
        break
      case 'service':
        cmp = (a.serviceName || a.serviceCode).localeCompare(b.serviceName || b.serviceCode, 'de')
        break
      case 'type':
        cmp = CHANGE_TYPE_LABEL[a.changeType].localeCompare(CHANGE_TYPE_LABEL[b.changeType], 'de')
        break
      case 'validFrom':
        cmp = a.validFrom.localeCompare(b.validFrom)
        break
      case 'seenAt':
        cmp = (a.seen?.seenAt ?? '').localeCompare(b.seen?.seenAt ?? '')
        break
    }
    // Same key: newest change first, so the order is stable across refreshes.
    return sign * cmp || b.changedAt.localeCompare(a.changedAt)
  })
}
